import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, LoadingButton, ContentSpinner, useToast } from '../../components/ui';

function ContentGeneratorStep4() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [projectId, setProjectId] = useState(null);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [contentData, setContentData] = useState(null);

  useEffect(() => {
    fetchAccounts();
    loadProjectData();
  }, []);

  const loadProjectData = async () => {
    const urlProjectId = searchParams.get('projectId');
    const currentProjectId = urlProjectId || sessionStorage.getItem('currentProjectId');

    if (!currentProjectId) {
      toast.warning('找不到專案，請重新開始');
      navigate('/templates/content-generator');
      return;
    }

    setProjectId(currentProjectId);

    if (currentProjectId) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/projects/${currentProjectId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const result = await response.json();
        if (result.success && result.project) {
          const project = result.project;
          console.log('📦 從 Supabase 載入 Project:', project.id);

          if (project.generatedImages) {
            setContentData({
              cards: project.generatedImages,
              visualCards: project.generatedImages,
              imageStyle: project.imageStyle,
              theme: project.theme,
              contentType: project.contentType || 'carousel'
            });
          }

          if (project.publishSettings) {
            const settings = project.publishSettings;
            if (settings.selectedAccounts) setSelectedAccounts(settings.selectedAccounts);
          }
          return;
        }
      } catch (error) {
        console.error('Failed to load project from Supabase:', error);
      }
    }

    const data = sessionStorage.getItem('contentToPublish');
    if (data) {
      const parsedData = JSON.parse(data);
      setContentData(parsedData);
    }
  };

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login first');
        navigate('/login');
        return;
      }

      const response = await fetch('/api/user/social-accounts/account-settings', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setAccounts(data.data || data.accounts || []);
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountToggle = (accountId) => {
    setSelectedAccounts(prev =>
      prev.includes(accountId) ? prev.filter(id => id !== accountId) : [...prev, accountId]
    );
  };

  const saveToDatabase = async () => {
    if (selectedAccounts.length === 0) {
      toast.warning('Please select at least one account');
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const contentToPublish = contentData || JSON.parse(sessionStorage.getItem('contentToPublish') || '{}');

      const libraryResponse = await fetch('/api/post-library/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content_type: 'carousel',
          content_data: contentToPublish.cards || [],
          visual_data: contentToPublish.visualCards || [],
          generated_from: 'content-generator',
          target_accounts: selectedAccounts
        })
      });

      const libraryResult = await libraryResponse.json();

      if (!libraryResult.success) {
        throw new Error(libraryResult.message || 'Failed to save to post library');
      }

      const currentProjectId = projectId || searchParams.get('projectId');
      if (currentProjectId) {
        try {
          await fetch(`/api/projects/${currentProjectId}/step`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              step: 4,
              generationStatus: 'saved',
              publishSettings: { selectedAccounts },
              selectedAccounts
            })
          });
          console.log('✅ Project updated to saved status');
        } catch (projectError) {
          console.error('Failed to update project status:', projectError);
        }
      }

      toast.success('Settings saved successfully!');

      setTimeout(() => {
        navigate('/posts');
      }, 1000);

    } catch (error) {
      console.error('Save failed:', error);
      toast.error(`Failed to save: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleGoHome = async () => {
    const currentProjectId = projectId || searchParams.get('projectId');
    if (currentProjectId) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`/api/projects/${currentProjectId}/step`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            step: 4,
            publishSettings: { selectedAccounts },
            selectedAccounts
          })
        });
        console.log('✅ Project step 4 saved to Supabase');
        toast.success('進度已儲存，可從「Your Projects」繼續編輯');
      } catch (error) {
        console.error('Failed to save project:', error);
        toast.warning('儲存失敗，請稍後再試');
      }
    } else {
      toast.warning('無法儲存進度');
    }

    navigate('/home');
  };

  const publishNow = async () => {
    if (selectedAccounts.length === 0) {
      toast.warning('Please select at least one account');
      return;
    }

    setPublishing(true);

    try {
      const token = localStorage.getItem('token');
      const contentToPublish = contentData || JSON.parse(sessionStorage.getItem('contentToPublish') || '{}');

      // 檢查是否有 visualCards（step3 產生的圖片）
      const visualCards = contentToPublish.visualCards || [];
      const hasImages = visualCards.length > 0 && visualCards.some(card => card.imageUrl);
      const isCarousel = contentToPublish.contentType === 'carousel' || hasImages;

      // 準備文字內容
      let textContent = '';
      if (contentToPublish.contentType === 'text') {
        textContent = contentToPublish.cards?.[0]?.description || '';
      } else {
        // 對於輪播貼文，使用第一頁的標題和描述作為貼文文字
        const firstCard = contentToPublish.cards?.[0];
        textContent = firstCard?.title || firstCard?.description || '';
      }

      const results = [];
      for (const accountId of selectedAccounts) {
        const account = accounts.find(a => a.id === accountId);
        if (!account || account.platform !== 'threads') continue;

        try {
          let response;

          if (isCarousel && hasImages && visualCards.length >= 2) {
            // 使用輪播 API 發布多張圖片
            const carouselItems = visualCards
              .filter(card => card.imageUrl)
              .map(card => ({
                type: 'IMAGE',
                url: card.imageUrl  // 後端期望 url 欄位
              }));

            console.log('📸 發布輪播貼文，圖片數量:', carouselItems.length);

            response = await fetch('/api/threads/post-carousel', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                accountId,
                items: carouselItems,
                text: textContent
              })
            });
          } else if (hasImages && visualCards.length === 1) {
            // 只有一張圖片，使用單圖 API
            const imageUrl = visualCards[0].imageUrl;
            console.log('🖼️ 發布單張圖片貼文:', imageUrl);

            response = await fetch('/api/threads/post-image', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                accountId,
                imageUrl,
                text: textContent
              })
            });
          } else {
            // 純文字貼文
            if (!textContent.trim()) {
              results.push({ accountId, success: false, error: 'No content to publish', accountName: account.account_name });
              continue;
            }

            console.log('📝 發布純文字貼文');

            response = await fetch('/api/threads/post-text', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                accountId,
                text: textContent
              })
            });
          }

          const result = await response.json();
          if (result.success) {
            results.push({ accountId, success: true, accountName: account.account_name });
          } else {
            results.push({ accountId, success: false, error: result.message || result.error, accountName: account.account_name });
          }
        } catch (err) {
          results.push({ accountId, success: false, error: err.message, accountName: account.account_name });
        }
      }

      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      if (successCount > 0) {
        toast.success(`Successfully published to ${successCount} account(s)!`);

        const currentProjectId = projectId || searchParams.get('projectId');
        if (currentProjectId) {
          try {
            await fetch(`/api/projects/${currentProjectId}/step`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                step: 4,
                generationStatus: 'published',
                publishSettings: { selectedAccounts },
                selectedAccounts
              })
            });
            console.log('✅ Project updated to published status');

            sessionStorage.removeItem('currentProjectId');
            sessionStorage.removeItem('contentGeneratorData');
            sessionStorage.removeItem('contentToPublish');
            sessionStorage.removeItem('generatedVisualCards');
          } catch (projectError) {
            console.error('Failed to update project status:', projectError);
          }
        }
      }
      if (failCount > 0) {
        const failedAccounts = results.filter(r => !r.success).map(r => r.accountName).join(', ');
        toast.error(`Failed to publish to: ${failedAccounts}`);
      }

      if (successCount > 0) {
        setTimeout(() => {
          navigate('/home');
        }, 1500);
      }

    } catch (error) {
      console.error('Publish failed:', error);
      toast.error(`Publish failed: ${error.message}`);
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-white flex items-center justify-center p-8">
      {/* Home Button - Top Left */}
      <Button
        onClick={handleGoHome}
        variant="ghost"
        size="sm"
        icon="mdi:arrow-left"
        className="absolute top-4 left-4 bg-gray-500/50 text-white hover:bg-gray-600/70"
      >
        Home
      </Button>

      {/* Back Button - Bottom Left */}
      <Button
        onClick={() => navigate(-1)}
        variant="ghost"
        size="sm"
        icon="mdi:arrow-left"
        className="absolute bottom-4 left-4 bg-gray-500/50 text-white hover:bg-gray-600/70"
      >
        Back
      </Button>

      <div className="max-w-[980px] w-full bg-white p-12">
        <div className="text-center mb-12">
          <h1 className="text-[2.5rem] font-bold text-black mb-2">Publish Settings</h1>
          <p className="text-base text-gray-500">Choose accounts to publish your content</p>
        </div>

        {/* Content Preview */}
        {contentData && (
          <div className="mb-12 p-6 bg-gray-50 border border-gray-200 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <Icon icon="mdi:text" className="text-blue-500" />
                Content Preview
              </div>
              <div className="text-xs text-slate-500 bg-gray-200 py-1 px-3 rounded">
                {contentData.contentType === 'text' ? 'Text Only' : 'Carousel'}
              </div>
            </div>
            <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap max-h-[200px] overflow-auto p-4 bg-white rounded-lg border border-gray-200">
              {contentData.contentType === 'text'
                ? contentData.cards?.[0]?.description || 'No content'
                : contentData.cards?.map(card => card.title || card.description).join('\n\n') || 'No content'
              }
            </div>
          </div>
        )}

        {/* Section 1: Select Accounts */}
        <div className="text-base font-semibold text-slate-800 mb-4">Select Accounts</div>

        {loading ? (
          <ContentSpinner text="Loading accounts..." />
        ) : accounts.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200 mb-12">
            <Icon icon="mdi:account-off" className="text-5xl text-slate-300" />
            <p className="mt-4 text-slate-400 text-sm">
              No accounts connected yet
            </p>
            <p className="mt-2 text-slate-500 text-xs">
              Please add an account from the sidebar
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 mb-12">
            {accounts.map(account => (
              <div
                key={account.id}
                onClick={() => handleAccountToggle(account.id)}
                className={`p-5 rounded-lg cursor-pointer transition-all duration-200 flex items-center gap-4 ${
                  selectedAccounts.includes(account.id)
                    ? 'bg-gray-50 border-2 border-black'
                    : 'bg-white border border-gray-200 hover:border-black'
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white text-xl flex-shrink-0">
                  <Icon icon={`mdi:${account.platform === 'threads' ? 'at' : account.platform}`} />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-800 mb-1">{account.account_name}</div>
                  <div className="text-xs text-slate-500">
                    {account.platform.charAt(0).toUpperCase() + account.platform.slice(1)}
                  </div>
                </div>
                {selectedAccounts.includes(account.id) && (
                  <Icon icon="mdi:check-circle" className="text-2xl text-black" />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 mt-8">
          {/* Save to Library Button */}
          <LoadingButton
            onClick={saveToDatabase}
            disabled={publishing || selectedAccounts.length === 0}
            loading={saving}
            loadingText="Saving..."
            icon="mdi:content-save"
            variant="secondary"
            size="lg"
            className="flex-1"
          >
            Save to Library
          </LoadingButton>

          {/* Publish Now Button */}
          <LoadingButton
            onClick={publishNow}
            disabled={saving || selectedAccounts.length === 0}
            loading={publishing}
            loadingText="Publishing..."
            icon="mdi:send"
            variant="accent"
            size="lg"
            className="flex-[1.5]"
          >
            Publish Now
          </LoadingButton>
        </div>

        {/* Info Box */}
        <div className="mt-6 p-5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 leading-relaxed">
          <strong>💡 功能說明：</strong><br />
          • <strong>Save to Library</strong> - 儲存內容到貼文庫，稍後審核或排程<br />
          • <strong>Publish Now</strong> - 立即發布到選定的 Threads 帳號
        </div>
      </div>
    </div>
  );
}

export default ContentGeneratorStep4;
